import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { PageHeader } from "../components/layout/PageHeader";
import {
  Search,
  Download,
  Mail,
  Package,
  CreditCard,
  LineChart,
  Bell,
  FileText,
  Plug,
} from "lucide-react";
import { useToast } from "../hooks/useToast";
import * as extensionsService from "../services/extensionsService";

const categoryIcons = {
  Stores: Package,
  Marketing: Mail,
  Payments: CreditCard,
  Accounting: FileText,
  Notifications: Bell,
  Analytics: LineChart,
};
const defaultIcon = Plug;

const categories = [
  { id: "all", nameKey: "extensions.allExtensions" },
  { id: "Stores", nameKey: "extensions.stores" },
  { id: "Marketing", nameKey: "extensions.marketing" },
  { id: "Payments", nameKey: "extensions.payments" },
  { id: "Analytics", nameKey: "extensions.analytics" },
  { id: "Accounting", nameKey: "extensions.accounting" },
  { id: "Notifications", nameKey: "extensions.notifications" },
];

const Extensions = () => {
  const { t } = useTranslation();
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedExtension, setSelectedExtension] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    extensionsService.fetchExtensions().then((data) => {
      if (!cancelled) setExtensions(Array.isArray(data) ? data : []);
    }).catch((err) => {
      if (!cancelled) {
        setError(err.message || "Failed to load extensions");
        setExtensions([]);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const filteredExtensions = extensions.filter((ext) => {
    const matchSearch = (ext.name || "").toLowerCase().includes(searchQuery.toLowerCase()) || (ext.description || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = categoryFilter === "all" || (ext.category || "") === categoryFilter;
    return matchSearch && matchCategory;
  });

  const handleInstall = (extension) => {
    toast({
      title: t("extensions.installationStarted"),
      description: t("extensions.installing", { name: extension.name }),
    });
  };

  return (
    <div className="animate-fadeIn">
      <PageHeader title={t("extensions.title")} sub={t("extensions.subtitle")} />

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{ maxWidth: 400, flex: "1 1 280px" }}>
          <div style={{ position: "relative" }}>
            <Search size={16} color="#6b7280" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <Input placeholder={t("extensions.searchExtensions")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ paddingLeft: 34 }} />
          </div>
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", minWidth: 180, fontSize: 14 }}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{t(c.nameKey)}</option>
          ))}
        </select>
      </div>

      {error && <p style={{ color: "#dc2626", marginBottom: 16 }}>{error}</p>}
      {loading && <p style={{ color: "#6b7280" }}>{t("extensions.loading")}</p>}
      {!loading && filteredExtensions.length === 0 && <p style={{ color: "#6b7280" }}>{t("extensions.noExtensions")}</p>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
        {filteredExtensions.map((extension) => {
          const Icon = categoryIcons[extension.category] || defaultIcon;
          return (
            <Card
              key={extension.id}
              style={{
                padding: "20px",
                cursor: "pointer",
                flex: "1 1 280px",
                maxWidth: "380px",
                minWidth: "260px",
              }}
              onClick={() => setSelectedExtension(extension)}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", height: "100%" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <div style={{ borderRadius: "12px", padding: "10px", background: "rgba(26, 31, 44, 0.1)", flexShrink: 0 }}>
                    <Icon size={22} color="#1A1F2C" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: "0 0 4px", fontSize: "16px", fontWeight: 600 }}>{extension.name}</h3>
                    <p style={{ margin: 0, fontSize: "13px", color: "#6b7280", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{extension.description}</p>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "auto" }}>
                  {extension.installed && (
                    <span style={{ fontSize: "12px", fontWeight: 600, color: "#15803d", background: "#dcfce7", padding: "2px 8px", borderRadius: 9999 }}>{t("extensions.installed")}</span>
                  )}
                  <span style={{ marginLeft: "auto", fontSize: "13px", color: "#6b7280" }}>{extension.category}</span>
                </div>
                <Button $size="sm" onClick={(e) => { e.stopPropagation(); handleInstall(extension); }} style={{ width: "100%" }} disabled={extension.installed}>
                  <Download size={14} />
                  {extension.installed ? t("extensions.installed") : t("extensions.install")}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <Modal
        open={!!selectedExtension}
        onClose={() => setSelectedExtension(null)}
        title={selectedExtension?.name ?? t("extensions.extension")}
        maxWidth="560px"
      >
        {selectedExtension && (() => {
          const ext = selectedExtension;
          const Icon = categoryIcons[ext.category] || defaultIcon;
          return (
            <div style={{ padding: "8px 0" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
                <div style={{ borderRadius: 12, padding: 14, background: "rgba(26, 31, 44, 0.1)" }}>
                  <Icon size={32} color="#1A1F2C" />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: 14, color: "#6b7280" }}>{ext.description}</p>
                  <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8, fontSize: 13, color: "#6b7280" }}>
                    <span>{ext.category}</span>
                    {ext.installed && <span style={{ color: "#15803d", fontWeight: 600 }}>· {t("extensions.installed")}</span>}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 24, display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={() => { handleInstall(ext); setSelectedExtension(null); }} disabled={ext.installed}>
                  <Download size={14} />
                  {ext.installed ? t("extensions.installed") : t("extensions.install")}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default Extensions;
